import React, { useState } from "react";
import { Link, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/reducers/userReducer";
import Swal from "sweetalert2";
import { useChatUnreadIndicator } from "../../../hooks/useChatUnreadIndicator";

const NAV_ITEMS = [
  { to: "/user/user-dashboard", icon: "🏠", label: "Dashboard" },
  { to: "/user/projects",         icon: "🚀", label: "Projects" },
  { to: "/user/tasks",            icon: "✅", label: "Tasks" },
  { to: "/user/messages",         icon: "💬", label: "Messages", badge: true },
  { to: "/user/invoices",         icon: "🧾", label: "Invoices" },
  { to: "/user/profile",          icon: "👤", label: "Profile" },
];

const ClientSideBarScreen = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { userData } = useSelector((store) => store.userReducer);
  const [collapsed, setCollapsed] = useState(false);
  const [hovItem, setHovItem] = useState(null);
  const [hovLogout, setHovLogout] = useState(false);

  const { unreadCount: chatUnreadCount, markChatsAsRead } = useChatUnreadIndicator(userData);
  const formattedChatBadge = chatUnreadCount > 99 ? "99+" : chatUnreadCount;
  const initials = (userData?.name || "U").split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();

  const handleLogout = () => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will be logged out of your account",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#4F46E5",
      cancelButtonColor: "#F87171",
      confirmButtonText: "Yes, logout",
    }).then((result) => {
      if (result.isConfirmed) {
        dispatch(logout());
        navigate("/login");
        Swal.fire({ title: "Logged out!", text: "You have been successfully logged out", icon: "success", timer: 1500, showConfirmButton: false });
      }
    });
  };

  const W = collapsed ? "72px" : "240px";

  // ── LIGHT THEME COLORS ──
  const c = {
    sidebar: "#FFFFFF",
    sidebarBorder: "#E8ECF4",
    bg: "#F4F6FD",
    text: "#1A202C",
    textMuted: "#718096",
    textFaint: "#A0AEC0",
    accent: "#4F46E5",
    accentLight: "#EEF2FF",
    accentBorder: "#C7D2FE",
    activeGrad: "linear-gradient(135deg, rgba(79,70,229,0.12), rgba(99,102,241,0.06))",
    navHov: "#F7F8FC",
    chipBg: "#EEF2FF",
    chipBorder: "#C7D2FE",
    toggleBg: "#F0F2FF",
    toggleBorder: "#E0E7FF",
    shadow: "4px 0 24px rgba(79,70,229,0.06)",
    tooltipBg: "#1E2040",
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh", fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        @keyframes fadeIn { from { opacity:0; transform:translateX(-8px); } to { opacity:1; transform:translateX(0); } }
        @keyframes float  { 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-4px); } }
        @keyframes pulse  { 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
      `}</style>

      {/* ── SIDEBAR ── */}
      <div style={{
        width: W, minHeight: "100vh", flexShrink: 0,
        background: c.sidebar,
        borderRight: "1px solid " + c.sidebarBorder,
        display: "flex", flexDirection: "column",
        transition: "width 0.25s cubic-bezier(.4,0,.2,1)",
        position: "sticky", top: 0,
        boxShadow: c.shadow,
        overflow: "hidden",
      }}>

        {/* ── Brand ── */}
        <div style={{
          padding: collapsed ? "20px 0" : "22px 18px 18px",
          borderBottom: "1px solid " + c.sidebarBorder,
          display: "flex", alignItems: "center",
          justifyContent: collapsed ? "center" : "space-between",
          gap: "10px",
        }}>
          {!collapsed && (
            <div style={{ display: "flex", alignItems: "center", gap: "10px", animation: "fadeIn 0.2s ease" }}>
              <div style={{
                width: 36, height: 36, borderRadius: "10px",
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "18px", boxShadow: "0 4px 12px rgba(79,70,229,0.3)",
                animation: "float 4s ease-in-out infinite", flexShrink: 0,
              }}>🤖</div>
              <span style={{ fontSize: "18px", fontWeight: 800, color: c.text, letterSpacing: "-0.4px" }}>AI Hub</span>
            </div>
          )}
          {collapsed && (
            <div style={{
              width: 36, height: 36, borderRadius: "10px",
              background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "18px", boxShadow: "0 4px 12px rgba(79,70,229,0.3)",
            }}>🤖</div>
          )}
          <button
            onClick={() => setCollapsed(!collapsed)}
            title={collapsed ? "Expand" : "Collapse"}
            style={{
              background: c.toggleBg, border: "1px solid " + c.toggleBorder,
              borderRadius: "8px", width: 28, height: 28, cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: c.accent, fontSize: "13px", flexShrink: 0,
              transition: "all 0.15s", fontWeight: 700,
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = c.accentLight; e.currentTarget.style.borderColor = c.accentBorder; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = c.toggleBg; e.currentTarget.style.borderColor = c.toggleBorder; }}
          >{collapsed ? "→" : "←"}</button>
        </div>

        {/* ── User chip ── */}
        {!collapsed && (
          <div style={{
            margin: "14px 12px 0",
            background: c.chipBg,
            border: "1px solid " + c.chipBorder,
            borderRadius: "12px", padding: "10px 12px",
            display: "flex", alignItems: "center", gap: "10px",
            animation: "fadeIn 0.25s ease",
          }}>
            <div style={{
              width: 34, height: 34, borderRadius: "10px", flexShrink: 0,
              background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "13px", fontWeight: 800, color: "#fff",
              boxShadow: "0 2px 8px rgba(79,70,229,0.25)",
            }}>{initials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                {userData?.name || "User"}
              </div>
            </div>
            <div style={{ width: 7, height: 7, borderRadius: "50%", background: "#10B981", boxShadow: "0 0 6px #10B981", flexShrink: 0, animation: "pulse 2.5s infinite" }} />
          </div>
        )}

        {/* ── Nav label ── */}
        {!collapsed && (
          <div style={{ padding: "18px 18px 8px", fontSize: "10px", fontWeight: 700, color: c.textFaint, letterSpacing: "0.12em", textTransform: "uppercase", animation: "fadeIn 0.3s ease" }}>
            Navigation
          </div>
        )}

        {/* ── Nav items ── */}
        <nav style={{ flex: 1, padding: collapsed ? "16px 10px" : "4px 10px", display: "flex", flexDirection: "column", gap: "2px" }}>
          {NAV_ITEMS.map((item) => {
            const active = location.pathname === item.to || location.pathname.startsWith(item.to + "/");
            const hov = hovItem === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => item.badge && markChatsAsRead?.()}
                onMouseEnter={() => setHovItem(item.to)}
                onMouseLeave={() => setHovItem(null)}
                style={{
                  display: "flex", alignItems: "center",
                  gap: collapsed ? 0 : "12px",
                  padding: collapsed ? "10px 0" : "10px 12px",
                  borderRadius: "12px",
                  justifyContent: collapsed ? "center" : "flex-start",
                  textDecoration: "none",
                  background: active ? c.activeGrad : hov ? c.navHov : "transparent",
                  border: active ? "1px solid " + c.accentBorder : "1px solid transparent",
                  boxShadow: active ? "0 2px 8px rgba(79,70,229,0.08)" : "none",
                  transition: "all 0.15s",
                  position: "relative",
                }}
              >
                {/* Active left bar */}
                {active && (
                  <div style={{ position: "absolute", left: 0, top: "20%", height: "60%", width: "3px", background: c.accent, borderRadius: "0 3px 3px 0" }} />
                )}

                <span style={{ fontSize: "17px", flexShrink: 0 }}>{item.icon}</span>

                {!collapsed && (
                  <span style={{ fontSize: "14px", fontWeight: active ? 700 : 500, color: active ? c.accent : hov ? c.text : c.textMuted, transition: "color 0.15s", flex: 1 }}>
                    {item.label}
                  </span>
                )}

                {/* Unread badge */}
                {item.badge && chatUnreadCount > 0 && (
                  <div style={{
                    background: "#EF4444", color: "#fff", borderRadius: "50px",
                    padding: "0 6px", fontSize: "10px", fontWeight: 700,
                    minWidth: "18px", height: "18px",
                    display: "inline-flex", alignItems: "center", justifyContent: "center",
                    boxShadow: "0 2px 6px rgba(239,68,68,0.4)",
                    position: collapsed ? "absolute" : "relative",
                    top: collapsed ? "4px" : "auto",
                    right: collapsed ? "4px" : "auto",
                  }}>{formattedChatBadge}</div>
                )}

                {/* Tooltip on collapsed */}
                {collapsed && hov && (
                  <div style={{
                    position: "absolute", left: "calc(100% + 12px)", top: "50%",
                    transform: "translateY(-50%)",
                    background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)",
                    borderRadius: "8px", padding: "6px 12px",
                    fontSize: "13px", fontWeight: 600, color: "#F8FAFC",
                    whiteSpace: "nowrap", zIndex: 100,
                    boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                    pointerEvents: "none",
                  }}>{item.label}</div>
                )}
              </Link>
            );
          })}
        </nav>

        {/* ── Logout ── */}
        <div style={{ padding: "12px 10px 20px", borderTop: "1px solid " + c.sidebarBorder }}>
          <button
            onClick={handleLogout}
            onMouseEnter={() => setHovLogout(true)}
            onMouseLeave={() => setHovLogout(false)}
            style={{
              width: "100%", display: "flex", alignItems: "center",
              gap: collapsed ? 0 : "12px",
              justifyContent: collapsed ? "center" : "flex-start",
              padding: collapsed ? "10px 0" : "10px 12px",
              borderRadius: "12px",
              border: hovLogout ? "1px solid #FECACA" : "1px solid transparent",
              background: hovLogout ? "#FEF2F2" : "transparent",
              cursor: "pointer", transition: "all 0.15s", position: "relative",
            }}
          >
            <span style={{ fontSize: "17px", flexShrink: 0 }}>🚪</span>
            {!collapsed && (
              <span style={{ fontSize: "14px", fontWeight: 600, color: hovLogout ? "#EF4444" : c.textMuted, transition: "color 0.15s" }}>
                Logout
              </span>
            )}
            {collapsed && hovLogout && (
              <div style={{
                position: "absolute", left: "calc(100% + 12px)", top: "50%",
                transform: "translateY(-50%)",
                background: "#1E293B", border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: "8px", padding: "6px 12px",
                fontSize: "13px", fontWeight: 600, color: "#F87171",
                whiteSpace: "nowrap", zIndex: 100,
                boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
                pointerEvents: "none",
              }}>Logout</div>
            )}
          </button>
        </div>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ flex: 1, minWidth: 0, background: "#F4F6FD" }}>
        <Outlet />
      </div>
    </div>
  );
};

export default ClientSideBarScreen;